import React, { useEffect, useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { Logo } from '../../components/Logo'

const NAV_LINKS_BEFORE_SOLUTIONS = [
  { to: '/platform', label: 'Platform' },
]

const SOLUTIONS_LINKS = [
  { to: '/solutions/health-plans', label: 'Health Plans' },
  { to: '/solutions/provider-organizations', label: 'Provider Organizations' },
  { to: '/solutions/practices', label: 'Practices' },
]

const NAV_LINKS_AFTER_SOLUTIONS = [
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/resources', label: 'Resources' },
  { to: '/security', label: 'Security' },
  { to: '/about', label: 'Company' },
]

export default function PublicLayout() {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()

  // Close the mobile menu on route change so it never sticks open across navigation.
  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  // Prevent background scroll while the mobile menu overlay is open.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [menuOpen])

  return (
    <div className="warm-theme">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <div className="public-nav">
        <div className="public-nav-inner">
          <Link to="/" className="logo" aria-label="ATUUNE home">
            <Logo size={40} className="nav-logo" />
          </Link>
          <nav className="public-nav-links" aria-label="Primary">
            {NAV_LINKS_BEFORE_SOLUTIONS.map((l) => (
              <Link key={l.to} className="navlink" to={l.to}>{l.label}</Link>
            ))}

            <div className="nav-dropdown">
              <button type="button" className="navlink" aria-haspopup="true">
                Solutions <ChevronDown size={14} aria-hidden="true" />
              </button>
              <div className="nav-dropdown-menu">
                {SOLUTIONS_LINKS.map((l) => (
                  <Link key={l.to} to={l.to}>{l.label}</Link>
                ))}
              </div>
            </div>

            {NAV_LINKS_AFTER_SOLUTIONS.map((l) => (
              <Link key={l.to} className="navlink" to={l.to}>{l.label}</Link>
            ))}
            <Link className="navlink login-link" to="/login">Log in</Link>
            <Link className="cta" to="/contact">Request Demo</Link>
          </nav>
          <button
            className="nav-toggle"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="mobile-menu" role="dialog" aria-modal="true">
          <nav aria-label="Mobile">
            {NAV_LINKS_BEFORE_SOLUTIONS.map((l) => (
              <Link key={l.to} className="mobile-navlink" to={l.to}>{l.label}</Link>
            ))}

            <div className="mobile-navlink mobile-nav-heading">Solutions</div>
            {SOLUTIONS_LINKS.map((l) => (
              <Link key={l.to} className="mobile-navlink mobile-navlink-sub" to={l.to}>{l.label}</Link>
            ))}

            {NAV_LINKS_AFTER_SOLUTIONS.map((l) => (
              <Link key={l.to} className="mobile-navlink" to={l.to}>{l.label}</Link>
            ))}
            <Link className="mobile-navlink" to="/login">Log in</Link>
            <Link className="cta mobile-cta" to="/contact">Request Demo</Link>
          </nav>
        </div>
      )}

      <main id="main-content">
        <Outlet />
      </main>

      <footer className="public-footer">
        <Logo size={28} className="footer-logo" />
        <div>Routing and continuity infrastructure for outpatient behavioral health.</div>
        <nav className="footer-links" aria-label="Legal">
          <Link to="/terms">Terms of Service</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/cookies">Cookie Policy</Link>
          <Link to="/security">Security</Link>
          <Link to="/contact">Contact</Link>
        </nav>
        <div className="disclaimer">
          ATUUNE does not provide medical advice or therapy. Not for emergencies.
        </div>
        <div className="copyright">© 2026 ATUUNE. All rights reserved.</div>
      </footer>
    </div>
  )
}
